import EventEmitter from './EventEmitter';
import Form from './Form';

/**
 * Modals, popups that show on the screen
 */
class Modal extends EventEmitter {
    /**
     * Initializes the static elements for modals
     */
    static Initialize() {
        nunjucks.configure('/templates/', {web: {useCache: true, async: true}});
        Modal.createElements();
    }

    /**
     * Creates the modal wrapper DOM elements
     */
    static createElements() {
        Modal.modalWrapperDomElement = document.createElement('div');
        Modal.modalWrapperDomElement.className = 'modal-wrapper';
        Modal.modalWrapperDomElement = document.body.appendChild(
            Modal.modalWrapperDomElement,
        );
    }

    /**
     * @param {string} templateName - Name of the template to render
     * @param {object} ctx - The context to provide to the template
     * @param {object} options - Options for the modal
     */
    constructor(templateName, ctx, options = {}) {
        super();

        options = Object.assign({}, {title: true}, options);

        this.templateName = templateName;
        this.ctx = ctx;
        this.loaded = false;
        this.title = options.title;
        this.autoInstantiateForms = options.autoInstantiateForms || true;

        this.modalDomElement = null;
        this.createModalElements();
    }

    /**
     * Creates the modal DOM elements
     */
    createModalElements() {
        this.modalDomElement = document.createElement('div');
        this.modalDomElement.className = 'modal';

        if (this.title === false) {
            this.modalDomElement.className += ' modal-wo-title';
        }
    }

    /**
     * Bind standard events to modal
     */
    async bindEvents() {
        const cancelDomElements = this.modalDomElement.querySelectorAll(
            '[data-modalresult="cancel"]',
        );

        const expandDomElements = this.modalDomElement.querySelectorAll(
            '[data-modalexpand]',
        );

        for (let i = 0; i < cancelDomElements.length; i++) {
            cancelDomElements[i].addEventListener('click',
                this.onCancelClick.bind(this));
        }

        for (let i = 0; i < expandDomElements.length; i++) {
            expandDomElements[i].addEventListener('click',
                this.onExpandClick.bind(this));
        }
    }

    /**
     * Load the template and render it with a previously set context
     */
    async load() {
        const html = await new Promise((resolve, reject) => {
            nunjucks.render(this.templateName, this.ctx, (err, res) => {
                if (!!err) {
                    reject(err);
                    return;
                }

                resolve(res);
            });
        });

        this.loaded = true;

        if (this.modalDomElement.parentNode !== Modal.modalWrapperDomElement) {
            this.modalDomElement = Modal.modalWrapperDomElement.appendChild(
                this.modalDomElement,
            );
        }

        this.modalDomElement.innerHTML = html;

        if (this.autoInstantiateForms === true) {
            this.instantiateForms();
        }

        await this.bindEvents();
    }

    /**
     * Instantiate all forms automatically
     */
    instantiateForms() {
        const formDomElements = this.modalDomElement.querySelectorAll('form');
        for (let i = 0; i < formDomElements.length; i++) {
            new Form(formDomElements[i]);
        }
    }

    /**
     * Set a validator for all forms
     * @param {function} validator - The function to run to validate the forms
     */
    setValidator(validator) {
        const formDomElements = this.modalDomElement.querySelectorAll('form');
        for (let i = 0; i < formDomElements.length; i++) {
            Form.getInstance(formDomElements[i]).setValidator(validator);
        }
    }

    /**
     * Re-render the modal, optionally with a new context
     * @param {object} ctx - The new context to use
     */
    async refresh(ctx = null) {
        if (!!ctx) {
            this.ctx = ctx;
        }

        return this.load();
    }

    /**
     * Toggle the modal fullscreen
     */
    toggleFullScreen() {
        this.modalDomElement.classList.toggle('modal-fullscreen');
    }

    /**
     * Show the modal
     */
    async show() {
        Modal.modalWrapperDomElement.classList.toggle('visible', true);

        if (this.loaded === false) {
            await this.load();
        }

        return new Promise((resolve) => {
            this.addEventListener('result', (e) => resolve(e.result));
        });
    }

    /**
     * Close the modal with a given result
     * @param {string} result - the result to pass
     */
    close(result = '') {
        Modal.modalWrapperDomElement.removeChild(this.modalDomElement);

        if (Modal.modalWrapperDomElement.childNodes.length === 0) {
            Modal.modalWrapperDomElement.classList.remove('visible');
        }

        this.emit({type: 'result', result: result, modal: this});
    }

    /**
     * Cancel click handler, closes the modal with a cancel result
     */
    onCancelClick() {
        this.close('cancel');
    }

    /**
     * Expand click handler, toggles fullscreen
     */
    onExpandClick() {
        this.toggleFullScreen();
    }
}

window.addEventListener('load', function () {
    Modal.Initialize();
});

export default Modal;
